import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  Input,
  linkedSignal,
  OnDestroy,
  OnInit,
  output
} from '@angular/core';
import { BmiStore } from '@bmi-store';
import { Bmi, BmiApi } from '@types-lib';
import { MetricToImperialHeightPipe, MetricToImperialWeightPipe, OrdinalDatePipe } from '@ui-lib/pipes';

@Component({
  selector: 'biometrics-display',
  imports: [CommonModule, OrdinalDatePipe, MetricToImperialHeightPipe, MetricToImperialWeightPipe],
  templateUrl: './biometrics-display.component.html',
  styleUrl: './biometrics-display.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BiometricsDisplayComponent implements OnInit, OnDestroy {
  @Input({ required: true }) userId!: number;
  readonly visitId = input<number>();
  @Input() showLogsButton = true;
  readonly viewLogsClick = output<MouseEvent>();
  restartPolling = input<boolean | null>();

  readonly store = inject(BmiStore);
  pollSignal = linkedSignal(() => {
    if (this.restartPolling() === null) {
      return true;
    }
    return !!this.restartPolling();
  });

  private readonly latestBmiItemFromStore = this.store.latestBmiItem;
  latestBmiItem = computed(() => (this.latestBmiItemFromStore && this.latestBmiItemFromStore()) || undefined);
  readonly fivePercentStatusDisplayValue = computed(() =>
    this.getFivePercentWeightLossStatusString(this.store.treatmentDetails.status()),
  );
  readonly fivePercentStatusColorClass = computed(() =>
    this.store.treatmentDetails.status()?.toLowerCase().replace('_', '-'),
  );

  ngOnInit() {
    this.store.startResourcePolling({
      resources: [{ reload: this.store.loadBmiDataResource, condition: this.pollSignal }],
    });
  }

  getFivePercentWeightLossStatusString(status: Bmi.FivePercentWeightLossStatusEnum | null): string {
    switch (status) {
      case Bmi.FivePercentWeightLossStatusEnum.DUE:
        return 'Due';
      case Bmi.FivePercentWeightLossStatusEnum.NOT_DUE:
        return 'Not Due';
      case Bmi.FivePercentWeightLossStatusEnum.MET:
        return 'Met';
      case Bmi.FivePercentWeightLossStatusEnum.NOT_MET:
        return 'Not Met';
      case Bmi.FivePercentWeightLossStatusEnum.TSD_UNKNOWN:
        return 'TSD unknown';
      default:
        return '';
    }
  }

  showBiometrics = computed<boolean>(
    () => !!this.latestBmiItem() && !Object.values(this.latestBmiItem() as BmiApi.UserBMI).every(val => !val),
  );

  onViewLogsClick(e: MouseEvent) {
    this.viewLogsClick.emit(e);
  }

  ngOnDestroy() {
    this.pollSignal.set(false);
  }
}
